// Purpose-made controls for the settings a generic control reads badly (spec/13 §13, 2026-10-07): a number with its
// unit (grace in minutes, a discount in %), small lists as chips, a role picker, a page picker, folder presets, a
// time range, the order sign-in codes try. Each takes and hands on exactly the stored value — seconds, basis points,
// role names, `ms-settings` page names, `HH:MM-HH:MM` — and converts only what it shows (`units.ts`).
//
// Which one a row gets is the layout's to say (`layout.ts` `display` and `control`); a row the layout says nothing
// about gets the schema's control (`controls.tsx`).

import { ORGANIZATION_ROLES } from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { Checkbox, ChipListEditor, Chip, Field, Input, SelectField, TextField, type ChipParse } from '@clavian/ui-web';
import { useEffect, useState, type ReactNode } from 'react';

import { jsonEqual } from '../lib/json';
import { SchemaControl } from './controls';
import type { ControlHint, DisplayUnit } from './layout';
import { enumOf, resolveSchema, type JsonSchema } from './schema';
import { errorKey, type ControlProps } from './types';
import {
  bestDurationUnit,
  bpsToPercentText,
  durationFromShown,
  durationToShown,
  durationUnitsFor,
  factorOf,
  percentTextToBps,
  type DurationUnit,
} from './units';

type Translate = ReturnType<typeof useTranslation>['t'];

export interface SettingControlProps extends ControlProps {
  readonly display?: DisplayUnit | undefined;
  readonly hint?: ControlHint | undefined;
}

/** The control a settings row draws: the layout's purpose-made one, else the schema's. */
export function SettingControl({ display, hint, ...props }: SettingControlProps): ReactNode {
  switch (hint) {
    case 'minutesList':
      return <MinutesListControl {...props} />;
    case 'spokenWarnings':
      return <SpokenWarningsControl {...props} />;
    case 'rolePicker':
      return <RolePickerControl {...props} />;
    case 'settingsPages':
      return <SettingsPagesControl {...props} />;
    case 'savePaths':
      return <SavePathsControl {...props} />;
    case 'fileNames':
      return <TextChipsControl {...props} kind="file" />;
    case 'extensionIds':
      return <TextChipsControl {...props} kind="extension" />;
    case 'updateWindow':
      return <UpdateWindowControl {...props} />;
    case 'channelOrder':
      return <ChannelOrderControl {...props} />;
    case 'version':
    case 'upiId':
    case 'prefix':
      return <PlaceholderTextControl {...props} hint={hint} />;
    default:
      break;
  }
  if (display !== undefined && isNumberSchema(props.schema)) {
    if (display.kind === 'duration') return <DurationControl {...props} display={display} />;
    if (display.kind === 'percentBps') return <PercentControl {...props} />;
    return <SuffixNumberControl {...props} display={display} />;
  }
  return <SchemaControl {...props} />;
}

const isNumberSchema = (schema: JsonSchema): boolean => schema['type'] === 'integer' || schema['type'] === 'number';

/** Report text that is not a value yet, so the form will not save it (as the money control does). */
function useUnparsed(props: ControlProps, bad: boolean): void {
  const key = errorKey(props.entryKey, props.path);
  const { onUnparsed } = props;
  useEffect(() => {
    if (!bad) return undefined;
    onUnparsed?.(key, true);
    return () => onUnparsed?.(key, false);
  }, [onUnparsed, key, bad]);
}

/**
 * Text that follows the value: when the value changes from outside — Discard, a reset, the fresh value after a 412 —
 * the text is rebuilt from it; while the person types, what they typed stays.
 */
function useFollowingText(value: unknown, show: (value: unknown) => string): [string, (text: string) => void, (value: unknown) => void] {
  const [text, setText] = useState(() => show(value));
  const [emitted, setEmitted] = useState<unknown>(value);
  if (!jsonEqual(value, emitted)) {
    setEmitted(value);
    setText(show(value));
  }
  return [text, setText, setEmitted];
}

const bounds = (schema: JsonSchema) => ({
  min: typeof schema['minimum'] === 'number' ? schema['minimum'] : undefined,
  max: typeof schema['maximum'] === 'number' ? schema['maximum'] : undefined,
});

// ---------------------------------------------------------------------------------------------
// Numbers with their unit
// ---------------------------------------------------------------------------------------------

/** A duration in the unit it reads best in: grace 300 seconds is "5 minutes"; 90 seconds stays "90 seconds". */
function DurationControl(props: ControlProps & { readonly display: Extract<DisplayUnit, { kind: 'duration' }> }) {
  const { t } = useTranslation('console');
  const { display, value, onChange } = props;
  const units = durationUnitsFor(display);
  const [unit, setUnit] = useState<DurationUnit>(() => (typeof value === 'number' ? bestDurationUnit(value, display) : (units[0] ?? display.stored)));
  const show = (next: unknown, inUnit: DurationUnit = unit) =>
    typeof next === 'number' && Number.isSafeInteger(next) && next % factorOf(inUnit, display.stored) === 0 ? String(durationToShown(next, inUnit, display.stored)) : '';
  const [text, setText, setEmitted] = useFollowingText(value, (next) => {
    // A value from outside reads in its best unit.
    if (typeof next === 'number') {
      const best = bestDurationUnit(next, display);
      if (best !== unit) setUnit(best);
      return show(next, best);
    }
    return '';
  });
  const [problem, setProblem] = useState<string | undefined>(undefined);
  useUnparsed(props, problem !== undefined);
  const { min, max } = bounds(props.schema);

  const emit = (raw: string, inUnit: DurationUnit) => {
    setText(raw);
    if (raw.trim() === '') {
      setProblem(props.required ? t('settings.unit.whole') : undefined);
      if (!props.required) {
        setEmitted(props.nullable ? null : undefined);
        onChange(props.nullable ? null : undefined);
      }
      return;
    }
    const parsed = durationFromShown(raw, inUnit, display.stored);
    if (!parsed.ok) {
      setProblem(t('settings.unit.whole'));
      return;
    }
    if ((min !== undefined && parsed.value < min) || (max !== undefined && parsed.value > max)) {
      const factor = factorOf(inUnit, display.stored);
      setProblem(
        t('settings.unit.between', {
          min: min === undefined ? 0 : Math.ceil(min / factor),
          max: max === undefined ? '' : Math.floor(max / factor),
        }),
      );
      return;
    }
    setProblem(undefined);
    setEmitted(parsed.value);
    onChange(parsed.value);
  };

  const word = (one: DurationUnit) => t(`settings.unit.word.${one}`);
  return (
    <Field label={props.label} description={props.description} error={problem ?? props.errorAt(props.path)} required={props.required} showOptional={!props.required}>
      {(control) => (
        <div className="console-unit-field">
          <Input
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            numeric
            inputMode="numeric"
            autoComplete="off"
            className="console-unit-field__input"
            value={text}
            disabled={props.disabled}
            onChange={(event) => emit(event.target.value, unit)}
          />
          {units.length > 1 ? (
            <select
              className="clav-input console-unit-field__unit"
              aria-label={`${props.label}: ${t('settings.control.durationUnit')}`}
              value={unit}
              disabled={props.disabled}
              onChange={(event) => {
                const next = event.target.value as DurationUnit;
                setUnit(next);
                // The same number of the new unit: "5" minutes becomes "5" seconds only if the person means it.
                if (text.trim() !== '') emit(text, next);
              }}
            >
              {units.map((one) => (
                <option key={one} value={one}>
                  {word(one)}
                </option>
              ))}
            </select>
          ) : (
            <span className="console-unit-field__word">{word(units[0] ?? display.stored)}</span>
          )}
        </div>
      )}
    </Field>
  );
}

/** Basis points as a percentage: 1000 is "10 %", typed as 10 or 12.5. */
function PercentControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const [text, setText, setEmitted] = useFollowingText(props.value, (next) => (typeof next === 'number' ? bpsToPercentText(next) : ''));
  const [problem, setProblem] = useState<string | undefined>(undefined);
  useUnparsed(props, problem !== undefined);
  const { min, max } = bounds(props.schema);
  const change = (raw: string) => {
    setText(raw);
    const parsed = percentTextToBps(raw);
    if (!parsed.ok) {
      setProblem(parsed.reason === 'fraction' ? t('settings.unit.percentFraction') : t('settings.unit.percentInvalid'));
      return;
    }
    if ((min !== undefined && parsed.value < min) || (max !== undefined && parsed.value > max)) {
      setProblem(t('settings.unit.percentInvalid'));
      return;
    }
    setProblem(undefined);
    setEmitted(parsed.value);
    props.onChange(parsed.value);
  };
  return (
    <Field label={props.label} description={props.description} error={problem ?? props.errorAt(props.path)} required={props.required}>
      {(control) => (
        <div className="console-unit-field">
          <Input
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            numeric
            inputMode="decimal"
            autoComplete="off"
            className="console-unit-field__input"
            value={text}
            disabled={props.disabled}
            onChange={(event) => change(event.target.value)}
          />
          <span className="console-unit-field__word">{t('settings.unit.word.percent')}</span>
        </div>
      )}
    </Field>
  );
}

/** A count or a temperature: the number as stored, with its word beside it. */
function SuffixNumberControl(props: ControlProps & { readonly display: Extract<DisplayUnit, { kind: 'count' | 'celsius' }> }) {
  const { t } = useTranslation('console');
  const word = props.display.kind === 'celsius' ? t('settings.unit.word.celsius') : t(`settings.unit.word.${props.display.per}`);
  const [text, setText, setEmitted] = useFollowingText(props.value, (next) => (typeof next === 'number' ? String(next) : ''));
  const [problem, setProblem] = useState<string | undefined>(undefined);
  useUnparsed(props, problem !== undefined);
  const { min, max } = bounds(props.schema);
  const change = (raw: string) => {
    setText(raw);
    const trimmed = raw.trim();
    const number = Number(trimmed);
    if (!/^\d+$/.test(trimmed) || !Number.isSafeInteger(number)) {
      setProblem(t('settings.unit.whole'));
      return;
    }
    if ((min !== undefined && number < min) || (max !== undefined && number > max)) {
      setProblem(t('settings.unit.between', { min: min ?? 0, max: max ?? '' }));
      return;
    }
    setProblem(undefined);
    setEmitted(number);
    props.onChange(number);
  };
  return (
    <Field label={props.label} description={props.description} error={problem ?? props.errorAt(props.path)} required={props.required}>
      {(control) => (
        <div className="console-unit-field">
          <Input
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            numeric
            inputMode="numeric"
            autoComplete="off"
            className="console-unit-field__input"
            value={text}
            disabled={props.disabled}
            onChange={(event) => change(event.target.value)}
          />
          <span className="console-unit-field__word">{word}</span>
        </div>
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------------------------
// Small lists as chips
// ---------------------------------------------------------------------------------------------

const asList = <T,>(value: unknown, keep: (item: unknown) => item is T): T[] => (Array.isArray(value) ? value.filter(keep) : []);
const isString = (item: unknown): item is string => typeof item === 'string';
const isNumber = (item: unknown): item is number => typeof item === 'number';

function itemSchema(props: ControlProps): JsonSchema {
  return resolveSchema((props.schema['items'] ?? {}) as JsonSchema, props.context).schema;
}

const maxItemsOf = (schema: JsonSchema): number => (typeof schema['maxItems'] === 'number' ? schema['maxItems'] : Infinity);

/** Minutes before a session ends: "10 min" chips, most first. */
function MinutesListControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const values = asList(props.value, isNumber);
  const { min, max } = bounds(itemSchema(props));
  const parse = (text: string): ChipParse<number> => {
    const trimmed = text.trim().replace(/\s*min(ute)?s?$/i, '');
    const number = Number(trimmed);
    if (!/^\d+$/.test(trimmed) || (min !== undefined && number < min) || (max !== undefined && number > max)) {
      return { ok: false, message: t('settings.control.minutesInvalid') };
    }
    return { ok: true, value: number };
  };
  return (
    <ChipListEditor<number>
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      values={values}
      onChange={props.onChange}
      format={(minutes) => t('settings.unit.shortMinutes', { count: minutes })}
      parse={parse}
      inputLabel={`${props.label}: ${t('settings.control.minutesInput')}`}
      placeholder={t('settings.control.minutesPlaceholder')}
      inputMode="numeric"
      emptyText={props.entryKey === 'sessions.staffAlertMinutes' ? t('settings.control.staffAlertsEmpty') : t('settings.control.minutesEmpty')}
      max={maxItemsOf(props.schema)}
      sort={(a, b) => b - a}
      disabled={props.disabled}
    />
  );
}

/** Spoken warnings: chosen from the time warnings (a spoken warning that is not a warning is never read out). */
function SpokenWarningsControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const spoken = asList(props.value, isNumber);
  const warnings = asList(props.valueOf?.('sessions.warningMinutes'), isNumber);
  const offered = [...new Set([...warnings, ...spoken])].sort((a, b) => b - a);
  const stray = spoken.filter((minutes) => !warnings.includes(minutes));
  const toggle = (minutes: number, on: boolean) => {
    const next = on ? [...spoken, minutes] : spoken.filter((one) => one !== minutes);
    props.onChange([...new Set(next)].sort((a, b) => b - a));
  };
  return (
    <Field as="group" label={props.label} description={props.description} error={props.errorAt(props.path)}>
      {() => (
        <div className="console-spoken">
          {offered.length === 0 ? <p className="console-muted">{t('settings.control.spokenEmpty')}</p> : null}
          <div className="console-chips">
            {offered.map((minutes) => (
              <Chip
                key={minutes}
                selected={spoken.includes(minutes)}
                {...(props.disabled ? {} : { onSelectedChange: (on: boolean) => toggle(minutes, on) })}
              >
                {t('settings.unit.shortMinutes', { count: minutes })}
              </Chip>
            ))}
          </div>
          {spoken.length === 0 && offered.length > 0 ? <p className="console-muted">{t('settings.control.spokenNone')}</p> : null}
          {stray.map((minutes) => (
            <p key={minutes} className="console-muted">
              {t('settings.control.spokenNotWarned', { minutes: t('settings.unit.shortMinutes', { count: minutes }) })}
            </p>
          ))}
        </div>
      )}
    </Field>
  );
}

/** Roles: the built-in ones offered by name, a custom one typed. */
function RolePickerControl(props: ControlProps) {
  const { t, i18n } = useTranslation('console');
  const values = asList(props.value, isString);
  const roleName = (role: string) => (i18n.exists(`settings.role.${role}`, { ns: 'console' }) ? t(`settings.role.${role}`) : role);
  const parse = (text: string): ChipParse<string> =>
    text.length >= 1 && text.length <= 64 ? { ok: true, value: text } : { ok: false, message: t('settings.control.valueInvalid') };
  return (
    <ChipListEditor<string>
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      values={values}
      onChange={props.onChange}
      format={roleName}
      parse={parse}
      inputLabel={`${props.label}: ${t('settings.control.roleInput')}`}
      placeholder={t('settings.control.rolePlaceholder')}
      emptyText={t('settings.control.rolesEmpty')}
      max={maxItemsOf(props.schema)}
      suggestions={ORGANIZATION_ROLES.map((role) => ({ value: role, label: roleName(role) }))}
      suggestionsLabel={t('settings.control.roleSuggest')}
      disabled={props.disabled}
    />
  );
}

/** The Windows Settings pages a player may open, offered by their plain names; another typed, for the rare case. */
export const KNOWN_SETTINGS_PAGES = [
  'sound',
  'mousetouchpad',
  'display',
  'keyboard',
  'bluetooth',
  'apps-volume',
  'easeofaccess-display',
  'easeofaccess-mouse',
  'regionlanguage',
] as const;

function SettingsPagesControl(props: ControlProps) {
  const { t, i18n } = useTranslation('console');
  const values = asList(props.value, isString);
  const pageName = (page: string) => (i18n.exists(`settings.page.${page}`, { ns: 'console' }) ? t(`settings.page.${page}`) : page);
  const minItems = typeof props.schema['minItems'] === 'number' ? props.schema['minItems'] : 0;
  const parse = (text: string): ChipParse<string> =>
    /^[a-z0-9-]{1,64}$/.test(text) ? { ok: true, value: text } : { ok: false, message: t('settings.control.pageInvalid') };
  return (
    <ChipListEditor<string>
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      values={values}
      onChange={props.onChange}
      format={pageName}
      parse={parse}
      inputLabel={`${props.label}: ${t('settings.control.pageInput')}`}
      placeholder={t('settings.control.pagePlaceholder')}
      max={maxItemsOf(props.schema)}
      suggestions={KNOWN_SETTINGS_PAGES.map((page) => ({ value: page, label: pageName(page) }))}
      suggestionsLabel={t('settings.control.pageSuggest')}
      // At least one page stays: the schema needs one.
      min={minItems}
      disabled={props.disabled}
    />
  );
}

/** Folders a Full clear keeps: the usual game-save places by name, a custom path for the rest. */
export const SAVE_PATH_PRESETS = [
  { path: '%UserProfile%\\Saved Games', name: 'savedGames' },
  { path: 'Documents\\My Games', name: 'myGames' },
  { path: '%AppData%', name: 'appDataRoaming' },
  { path: '%LocalAppData%', name: 'appDataLocal' },
  { path: '%UserProfile%\\AppData\\LocalLow', name: 'appDataLocalLow' },
] as const;

function SavePathsControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const values = asList(props.value, isString);
  const nameOf = (path: string) => {
    const preset = SAVE_PATH_PRESETS.find((one) => one.path.toLowerCase() === path.toLowerCase());
    return preset === undefined ? path : t(`settings.path.${preset.name}`);
  };
  const parse = (text: string): ChipParse<string> =>
    text.length >= 1 && text.length <= 260 ? { ok: true, value: text } : { ok: false, message: t('settings.control.pathInvalid') };
  return (
    <ChipListEditor<string>
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      values={values}
      onChange={props.onChange}
      format={nameOf}
      parse={parse}
      inputLabel={`${props.label}: ${t('settings.control.pathInput')}`}
      placeholder={t('settings.control.pathPlaceholder')}
      emptyText={t('settings.control.pathsEmpty')}
      max={maxItemsOf(props.schema)}
      suggestions={SAVE_PATH_PRESETS.map((preset) => ({ value: preset.path, label: t(`settings.path.${preset.name}`) }))}
      suggestionsLabel={t('settings.control.pathSuggest')}
      disabled={props.disabled}
    />
  );
}

/** A list of short texts — program file names, extension ids — as chips, each checked before it joins. */
function TextChipsControl(props: ControlProps & { readonly kind: 'file' | 'extension' }) {
  const { t } = useTranslation('console');
  const values = asList(props.value, isString);
  const item = itemSchema(props);
  const maxLength = typeof item['maxLength'] === 'number' ? item['maxLength'] : 256;
  const pattern = typeof item['pattern'] === 'string' ? new RegExp(item['pattern']) : undefined;
  const words =
    props.kind === 'file'
      ? { input: 'fileInput', placeholder: 'filePlaceholder', invalid: 'fileInvalid', empty: 'filesEmpty' }
      : { input: 'extensionInput', placeholder: 'extensionPlaceholder', invalid: 'extensionInvalid', empty: 'extensionsEmpty' };
  const parse = (text: string): ChipParse<string> =>
    text.length >= 1 && text.length <= maxLength && (pattern === undefined || pattern.test(text))
      ? { ok: true, value: text }
      : { ok: false, message: t(`settings.control.${words.invalid}`) };
  return (
    <ChipListEditor<string>
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      values={values}
      onChange={props.onChange}
      parse={parse}
      inputLabel={`${props.label}: ${t(`settings.control.${words.input}`)}`}
      placeholder={t(`settings.control.${words.placeholder}`)}
      emptyText={t(`settings.control.${words.empty}`)}
      max={maxItemsOf(props.schema)}
      disabled={props.disabled}
    />
  );
}

/**
 * Any other list of plain values (a string or a number per item), as chips — never "Item 1 / Add item" rows. The
 * item's schema decides what is accepted.
 */
export function ScalarChipsControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const item = itemSchema(props);
  const numeric = item['type'] === 'integer' || item['type'] === 'number';
  const values = asList(props.value, (entry): entry is string | number => typeof entry === 'string' || typeof entry === 'number');
  const { min, max } = bounds(item);
  const maxLength = typeof item['maxLength'] === 'number' ? item['maxLength'] : Infinity;
  const minLength = typeof item['minLength'] === 'number' ? item['minLength'] : 1;
  const pattern = typeof item['pattern'] === 'string' ? new RegExp(item['pattern']) : undefined;
  const parse = (text: string): ChipParse<string | number> => {
    if (numeric) {
      const number = Number(text);
      const whole = item['type'] !== 'integer' || Number.isInteger(number);
      return Number.isFinite(number) && whole && (min === undefined || number >= min) && (max === undefined || number <= max)
        ? { ok: true, value: number }
        : { ok: false, message: t('settings.control.valueInvalid') };
    }
    return text.length >= minLength && text.length <= maxLength && (pattern === undefined || pattern.test(text))
      ? { ok: true, value: text }
      : { ok: false, message: t('settings.control.valueInvalid') };
  };
  return (
    <ChipListEditor<string | number>
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      values={values}
      onChange={props.onChange}
      parse={parse}
      inputLabel={`${props.label}: ${t('settings.control.addValue')}`}
      inputMode={numeric ? 'numeric' : 'text'}
      emptyText={t('settings.control.emptyList')}
      max={maxItemsOf(props.schema)}
      disabled={props.disabled}
    />
  );
}

// ---------------------------------------------------------------------------------------------
// The update window, the order of sign-in codes, and texts with an example
// ---------------------------------------------------------------------------------------------

const WINDOW = /^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/;

/** `HH:MM-HH:MM` or none: "Any time", or "Only between [03:00] and [06:00]". */
function UpdateWindowControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const value = typeof props.value === 'string' && WINDOW.test(props.value) ? props.value : null;
  const [from, to] = value === null ? ['', ''] : (value.split('-') as [string, string]);
  const [draft, setDraft] = useState({ from, to, limited: value !== null });
  const [seen, setSeen] = useState<unknown>(props.value);
  if (!jsonEqual(seen, props.value)) {
    setSeen(props.value);
    setDraft({ from, to, limited: value !== null });
  }
  const incomplete = draft.limited && (draft.from === '' || draft.to === '');
  useUnparsed(props, incomplete);
  const set = (next: typeof draft) => {
    setDraft(next);
    if (!next.limited) {
      setSeen(null);
      props.onChange(null);
    } else if (next.from !== '' && next.to !== '') {
      const joined = `${next.from}-${next.to}`;
      setSeen(joined);
      props.onChange(joined);
    }
  };
  return (
    <Field as="group" label={props.label} description={props.description} error={incomplete ? t('settings.control.windowInvalid') : props.errorAt(props.path)}>
      {() => (
        <div className="console-window">
          <Checkbox
            label={t('settings.control.windowBetween')}
            checked={draft.limited}
            disabled={props.disabled}
            onCheckedChange={(checked) => set({ ...draft, limited: checked === true, from: draft.from || '03:00', to: draft.to || '06:00' })}
          />
          {draft.limited ? (
            <div className="console-range">
              <Input type="time" numeric aria-label={`${props.label}: ${t('settings.control.windowFrom')}`} value={draft.from} disabled={props.disabled} onChange={(event) => set({ ...draft, from: event.target.value })} />
              <span className="console-range__arrow" aria-hidden="true">
                –
              </span>
              <Input type="time" numeric aria-label={`${props.label}: ${t('settings.control.windowTo')}`} value={draft.to} disabled={props.disabled} onChange={(event) => set({ ...draft, to: event.target.value })} />
              {draft.from !== '' && draft.to !== '' && draft.to < draft.from ? <span className="console-range__note">{t('settings.hours.nextDay')}</span> : null}
            </div>
          ) : (
            <p className="console-muted">{t('settings.control.windowAnyTime')}</p>
          )}
        </div>
      )}
    </Field>
  );
}

/** The order codes try their channels in, as the four orders there are. */
const CHANNEL_ORDERS: readonly (readonly [string, readonly string[]])[] = [
  ['whatsappFirst', ['whatsapp', 'sms']],
  ['smsFirst', ['sms', 'whatsapp']],
  ['whatsappOnly', ['whatsapp']],
  ['smsOnly', ['sms']],
];

function ChannelOrderControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const current = CHANNEL_ORDERS.find(([, order]) => jsonEqual(order, props.value))?.[0];
  // A stored order this list does not name (none can be, by the schema) still shows rather than vanishes.
  if (current === undefined && Array.isArray(props.value) && props.value.length > 0) return <SchemaControl {...props} />;
  return (
    <SelectField
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      disabled={props.disabled}
      options={CHANNEL_ORDERS.map(([id]) => ({ value: id, label: t(`settings.control.channelOrder.${id}`) }))}
      {...(current === undefined ? {} : { value: current })}
      onValueChange={(id) => {
        const order = CHANNEL_ORDERS.find(([one]) => one === id)?.[1];
        if (order !== undefined) props.onChange([...order]);
      }}
    />
  );
}

/** A text with an example of what goes in it: a version, a UPI ID, a document prefix. */
function PlaceholderTextControl(props: ControlProps & { readonly hint: 'version' | 'upiId' | 'prefix' }) {
  const { t } = useTranslation('console');
  const placeholder = { version: 'versionPlaceholder', upiId: 'upiPlaceholder', prefix: 'prefixPlaceholder' }[props.hint];
  const text = typeof props.value === 'string' ? props.value : '';
  return (
    <TextField
      className="console-input--medium"
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      required={props.required}
      showOptional={!props.required}
      value={text}
      placeholder={t(`settings.control.${placeholder}`)}
      disabled={props.disabled}
      autoComplete="off"
      spellCheck={false}
      onChange={(event) => {
        const raw = props.hint === 'prefix' ? event.target.value.toUpperCase() : event.target.value;
        props.onChange(raw === '' ? (props.nullable ? null : props.required ? '' : undefined) : raw);
      }}
    />
  );
}

/** Is this a list the chips read better for — plain values, no choices, no shapes? */
export function isScalarList(schema: JsonSchema, props: Pick<ControlProps, 'context'>): boolean {
  if (schema['type'] !== 'array') return false;
  const items = resolveSchema((schema['items'] ?? {}) as JsonSchema, props.context).schema;
  return (items['type'] === 'string' || items['type'] === 'integer' || items['type'] === 'number') && enumOf(items) === undefined;
}

export type { Translate };
